import React, { useState } from 'react';
import { View, TouchableOpacity, TextInput, ScrollView, ActivityIndicator } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { translate } from '../../services/translate';
import MyAppText from '../MyAppText';
import getBestContrastColor from '../../services/best-contrast-color';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { FieldValues, Path, useController, useFormContext } from 'react-hook-form';
import { Hoverable } from 'react-native-web-hover';
import TooltipWrapper from '../TooltipWrapper';

export interface FilterListItem {
    label: string;
    value: number | string;
    color?: string;
    tooltipText?: string;
}

type Props<
    TFieldValues extends FieldValues,
    TName extends Path<TFieldValues>
> = {
    items: FilterListItem[];
    name: TName;
    renderBadge?: boolean;
    label?: string;
    isLoading?: boolean;
} & ({
    showSelectedItems?: false;
    visibleLimit: never;
} | {
    showSelectedItems?: true;
    visibleLimit?: number;
});


function CheckboxList<
    TFieldValues extends FieldValues,
    TName extends Path<TFieldValues>,
>({ items, name, renderBadge, label, isLoading, showSelectedItems, visibleLimit = 10 }: Props<TFieldValues, TName>) {
    const { styles, theme } = useStyles(styleSheet);
    const [showAll, setShowAll] = useState(false);

    const { control } = useFormContext<TFieldValues>();

    const { field } = useController({
        control,
        name,
    });

    const [search, setSearch] = useState('');

    const value = field.value as (number | string)[];

    const selectedItems = items.filter(item => value.includes(item.value));
    const visibleSelected = showAll ? selectedItems : selectedItems.slice(0, visibleLimit);
    const hiddenCount = selectedItems.length - visibleLimit;

    const filtered = items.filter(item =>
        item.label.toLowerCase().includes(search.toLowerCase())
    );

    const toggleItem = (id: number | string) => {
        if (value.includes(id)) {
            field.onChange(value.filter(v => v !== id));
        } else {
            field.onChange([...value, id]);
        }
    };

    return (
        <View style={styles.container}>
            {label && <MyAppText style={styles.filterLabel}>{label}</MyAppText>}

            {items.length >= 7 && (
                <View style={styles.searchContainer}>
                    <FontAwesomeIcon icon={faMagnifyingGlass as IconDefinition} fontSize={16} color={theme.colors.neutral['300']} />
                    <TextInput
                        style={styles.searchInput}
                        placeholder={translate('typeToSearch')}
                        placeholderTextColor={theme.colors.neutral['300']}
                        value={search}
                        onChangeText={setSearch}
                    />
                </View>
            )}

            <View style={styles.countRow}>
                <MyAppText style={styles.countText}>
                    {translate('items')}: {filtered.length}
                </MyAppText>
                {value.length === 0 ? (
                    <TouchableOpacity onPress={() => field.onChange(filtered.map(i => i.value))}>
                        <MyAppText style={styles.actionText}>
                            {translate('selectAll')}
                        </MyAppText>
                    </TouchableOpacity>
                ) : (
                    <TouchableOpacity onPress={() => field.onChange([])}>
                        <MyAppText style={styles.actionText}>
                            {translate('clearSelection')}
                        </MyAppText>
                    </TouchableOpacity>
                )}
            </View>

            {isLoading ? (
                <View style={styles.loadingContainer}>
                    <ActivityIndicator size='small' color={theme.colors.primary['500']} />
                </View>
            ) : (
                <ScrollView
                    style={styles.list}
                    contentContainerStyle={{ gap: 8 }}
                    nestedScrollEnabled
                >
                    {filtered.map((item) => {
                        const selected = value.includes(item.value);
                        return (
                            <TouchableOpacity
                                key={item.value}
                                style={styles.row}
                                onPress={() => toggleItem(item.value)}
                            >
                                <View style={[styles.checkbox, selected && styles.checkboxSelected]}>
                                    {selected && <FontAwesomeIcon icon={faCheck as IconDefinition} fontSize={10} color={theme.colors.white['500']} />}
                                </View>
                                <View style={styles.itemLabelContainer}>
                                    {renderBadge ? (
                                        <View style={[styles.badge, { backgroundColor: item.color ?? theme.colors.neutral['300'] }]}>
                                            <MyAppText style={styles.badgeText(item.color ?? theme.colors.neutral['300'])}>{item.label}</MyAppText>
                                        </View>
                                    ) : (
                                        <MyAppText style={styles.label}>{item.label}</MyAppText>
                                    )}
                                    {item.tooltipText && <Hoverable>
                                        {({ hovered }) => <TooltipWrapper hovered={hovered} tooltipText={item.tooltipText!} />}
                                    </Hoverable>}
                                </View>
                            </TouchableOpacity>
                        );
                    })}
                </ScrollView>
            )}
            {showSelectedItems && selectedItems.length > 0 && (
                <>
                    <View style={styles.selectedBadgesContainer}>
                        {visibleSelected.map((item) => (
                            <View key={String(item.value)} style={styles.selectedBadge}>
                                <MyAppText style={styles.selectedBadgeText}>{item.label}</MyAppText>
                                <TouchableOpacity onPress={() => toggleItem(item.value)}>
                                    <FontAwesomeIcon icon={faXmark as IconDefinition} fontSize={12} color={theme.colors.labelColor} />
                                </TouchableOpacity>
                            </View>
                        ))}
                    </View>

                    {selectedItems.length > visibleLimit && (
                        <TouchableOpacity onPress={() => setShowAll(!showAll)}>
                            <MyAppText style={styles.toggleText}>
                                {showAll ? translate('seeLess') : `${translate('seeAll')} (+${hiddenCount})`}
                            </MyAppText>
                        </TouchableOpacity>
                    )}
                </>
            )}
        </View>
    );
}

CheckboxList.getCount = (value: (number | string)[]) => {
    return value.length;
};

const styleSheet = createStyleSheet((theme) => ({
    container: { gap: 12 },
    filterLabel: {
        fontSize: 16,
        fontWeight: '600',
        color: theme.colors.labelColor,
    },
    searchContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        borderWidth: 1,
        borderColor: theme.colors.neutral['300'],
        borderRadius: 8,
        paddingHorizontal: 12,
        paddingVertical: 10,
        gap: 8,
    },
    searchInput: {
        flex: 1,
        fontSize: 16,
        color: theme.colors.neutral['400'],
        outlineStyle: 'none',
    },
    countRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    countText: {
        fontSize: 14,
        fontWeight: 'bold',
        color: theme.colors.labelColor,
    },
    actionText: {
        fontSize: 14,
        fontWeight: 'bold',
        color: theme.colors.primary['500'],
    },
    loadingContainer: {
        paddingVertical: 20,
        alignItems: 'center',
        justifyContent: 'center',
    },
    list: {
        maxHeight: 208,
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
    },
    itemLabelContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
    },
    checkbox: {
        width: 20,
        height: 20,
        borderRadius: 4,
        borderWidth: 1,
        borderColor: theme.colors.neutral['300'],
        alignItems: 'center',
        justifyContent: 'center',
    },
    checkboxSelected: {
        backgroundColor: theme.colors.primary['500'],
        borderColor: theme.colors.primary['500'],
    },
    badge: {
        paddingHorizontal: 12,
        paddingVertical: 4,
        borderRadius: 20,
    },
    badgeText: (tagColor: string) => ({
        color: getBestContrastColor(tagColor),
        fontSize: 14,
        fontWeight: '600',
    }),
    label: {
        fontSize: 16,
        color: theme.colors.labelColor,
    },
    selectedBadgesContainer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
    },
    selectedBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        backgroundColor: theme.colors.neutral['200'],
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 20,
    },
    selectedBadgeText: {
        fontSize: 14,
        color: theme.colors.labelColor,
    },
    toggleText: {
        fontSize: 14,
        fontWeight: 'bold',
        color: theme.colors.primary['500'],
    },
}));

export default CheckboxList;
